import { FabricText, Group, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { FillValue, ToolRecipe } from '../types';
import { CATEGORY_COLORS, CATEGORY_LABELS, ELEMENTS } from '../data/elements';
import type { ElementCategory, ElementInfo } from '../data/elements';

/**
 * The Periodic Table of the Elements — the full 18-column × 7-period grid with
 * the f-block (lanthanides + actinides) detached below, marked by the standard
 * asterisk / double-asterisk in the group-3 cells of periods 6 and 7 (SM-F2,
 * 2026-07-23, owner: "Perhaps we could even expand and include a complete
 * periodic table as an option?").
 *
 * A poster centerpiece: sized from a fixed overall width and inserted at
 * ~35% of the workspace (smart objects scale after insert), so ~500 grouped
 * fabric primitives are expected and fine. Every cell is a category- (or
 * state-) colored Rect + atomic number + symbol, with optional name and mass
 * (auto-scaled to the cell). colorBy switches the whole map between the family
 * scheme (CATEGORY_COLORS) and a solid/liquid/gas scheme. `highlight` rings a
 * teacher-named set of symbols. An optional legend row explains the colors.
 *
 * Emits Rect + FabricText only, grouped and tagged {tool,version,params} —
 * plain fabric JSON, vector at any export dpi. version 1 (new recipe).
 *
 * BUILD A/B (granular control, owner 2026-07-23: "much more granular control of
 * the background colors for different element families, the fonts for each
 * specific part"): each family (and each state) is now an independent 'fill'
 * param — solid, gradient or texture — defaulting to the scheme hex; and the
 * cell symbol / name / mass / number and the legend each take their own font,
 * defaulting to '' = inherit the base Text font. Both are strictly additive:
 * the defaults reproduce the old scheme byte-for-byte, so version stays 1 (no
 * regenerate churn — same params render identically).
 */

// ---------------------------------------------------------------------------
// Defensive coercion (params arrive untrusted; generate() never throws / NaNs).
// ---------------------------------------------------------------------------

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Empty string is meaningful for `title` / `highlight` (a blank is allowed). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Black or white ink, whichever reads on the given #rrggbb background. */
function readableInkOn(bg: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(bg).trim());
  if (!m) return '#0f172a';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#0f172a' : '#ffffff';
}

/** Shrink a font so `s` fits `maxW` at ~`em` average advance; never below 6. */
function fitFont(s: string, base: number, maxW: number, em = 0.58): number {
  const len = Math.max(1, s.length);
  const need = len * base * em;
  if (need <= maxW || maxW <= 0) return base;
  return Math.max(6, maxW / (len * em));
}

interface Txt {
  x: number;
  y: number;
  size: number;
  font: string;
  fill: string;
  originX?: 'left' | 'center' | 'right';
  originY?: 'top' | 'center' | 'bottom';
  weight?: string | number;
}

function text(content: string, o: Txt): FabricText {
  return new FabricText(content, {
    left: o.x,
    top: o.y,
    fontSize: o.size,
    fontFamily: o.font,
    fill: o.fill,
    originX: o.originX ?? 'left',
    originY: o.originY ?? 'top',
    fontWeight: o.weight ?? 'normal',
  });
}

const INK = '#0f172a';
const DEFAULT_FONT = 'Arial';
const CELL_FALLBACK = '#e2e8f0';
const HIGHLIGHT = '#f59e0b'; // amber ring — pops on any category/state color

// Solid/liquid/gas scheme (local — kept light so dark cell text reads).
const STATE_COLORS: Record<string, string> = {
  solid: '#cbd5e1',
  liquid: '#7dd3fc',
  gas: '#fca5a5',
};
const STATE_LABELS: Record<string, string> = {
  solid: 'Solid',
  liquid: 'Liquid',
  gas: 'Gas',
};

// ---------------------------------------------------------------------------
// Granular per-family / per-state cell colors (BUILD A, owner: "much more
// granular control of the background colors for different element families").
// Each family (and each state) is an independent 'fill' param defaulting to the
// scheme hex, so a teacher can recolor one family — or drop a gradient/texture
// on it — without touching the rest. cell + legend + f-block placeholder all
// resolve through the SAME map so a recolor is consistent everywhere.
// ---------------------------------------------------------------------------

/** Pull one representative solid color out of any FillValue — for the readable
 *  ink computation and the applyFill fallback (a family/state may now wear a
 *  gradient or texture, but text contrast + the safety fallback want a plain
 *  color). Mirrors the elementCard helper. */
function solidFromFill(fill: unknown, fallback: string): string {
  if (typeof fill === 'string') return fill.trim() || fallback;
  if (fill && typeof fill === 'object') {
    const f = fill as {
      type?: string;
      color?: string;
      from?: string;
      stops?: { color?: string }[];
    };
    if (f.type === 'solid' && typeof f.color === 'string') return f.color;
    if (f.type === 'linear-gradient') {
      if (Array.isArray(f.stops) && f.stops.length && f.stops[0].color) return f.stops[0].color;
      if (typeof f.from === 'string') return f.from;
    }
    if (f.type === 'radial-gradient' && Array.isArray(f.stops) && f.stops.length && f.stops[0].color) {
      return f.stops[0].color;
    }
  }
  return fallback;
}

interface FillJob {
  obj: FabricObject;
  fill: FillValue;
  fallback: string;
}

/** category → per-family fill param key. */
const FAM_PARAM_KEY: Record<ElementCategory, string> = {
  'alkali-metal': 'famAlkali',
  'alkaline-earth': 'famAlkalineEarth',
  'transition-metal': 'famTransition',
  'post-transition-metal': 'famPostTransition',
  'metalloid': 'famMetalloid',
  'reactive-nonmetal': 'famNonmetal',
  'halogen': 'famHalogen',
  'noble-gas': 'famNobleGas',
  'lanthanide': 'famLanthanide',
  'actinide': 'famActinide',
  'unknown': 'famUnknown',
};

/** state → per-state fill param key. */
const STATE_PARAM_KEY: Record<string, string> = {
  solid: 'stateSolidFill',
  liquid: 'stateLiquidFill',
  gas: 'stateGasFill',
};

interface CellPaint {
  fill: FillValue;
  solid: string;
}

/** Resolve one fill param key to a {FillValue, representative solid} pair,
 *  falling back to the scheme hex when the param is absent or blank — so a
 *  default table (or one saved before granular colors existed) paints
 *  byte-identically to the old inline-color code. */
function pickFill(params: Record<string, unknown>, key: string, fallback: string): CellPaint {
  const raw = params[key];
  const fill: FillValue =
    raw == null || (typeof raw === 'string' && raw.trim() === '')
      ? fallback
      : (raw as FillValue);
  return { fill, solid: solidFromFill(fill, fallback) };
}

// ---- Layout math ----------------------------------------------------------
// 18 columns from a fixed overall width; square cells on a uniform pitch. The
// 7-period main grid sits under the title; the 15-wide lanthanide + actinide
// strips detach one pitch-gap below it, starting under group 3 (the standard
// depiction). Legend (optional) rides the bottom. Result is wider than tall.
const OVERALL_W = 1980;
const COLS = 18;
const PITCH = OVERALL_W / COLS; // 110
const GAP = 10;
const CELL = PITCH - GAP; // 100
const TITLE_H = PITCH * 1.5;
const GRID_TOP = TITLE_H;
const MAIN_ROWS = 7;
const MAIN_BOTTOM = GRID_TOP + MAIN_ROWS * PITCH;
const FGAP = PITCH * 0.55;
const LANTH_TOP = MAIN_BOTTOM + FGAP;
const ACTIN_TOP = LANTH_TOP + PITCH;
const DETACH_BOTTOM = ACTIN_TOP + PITCH;
const FBLOCK_COL0 = 2; // strips start under group 3
const LEGEND_ROW_H = PITCH * 0.52;
const LEGEND_PER_ROW = 6;

interface CellStyle {
  colorBy: 'category' | 'state';
  showNames: boolean;
  showMasses: boolean;
  // Per-part fonts (BUILD B): each inherits textFont unless the teacher sets it.
  symbolFont: string;
  nameFont: string;
  massFont: string;
  numberFont: string;
}

/** One element cell: colored Rect + z + symbol (+ name +mass). Highlighted
 *  cells pop slightly larger and wear a thick amber ring. The cell surface
 *  paints from the resolved family/state fill (gradients/textures supported via
 *  the deferred applyFill); ink reads on that fill's representative solid. */
function drawCell(
  e: ElementInfo,
  x: number,
  y: number,
  cs: CellStyle,
  highlighted: boolean,
  paint: CellPaint,
  out: FabricObject[],
  fills: FillJob[],
): void {
  const ink = readableInkOn(paint.solid);
  const size = highlighted ? CELL * 1.06 : CELL;
  const ox = x - (size - CELL) / 2;
  const oy = y - (size - CELL) / 2;

  const cell = new Rect({
    left: ox,
    top: oy,
    width: size,
    height: size,
    rx: CELL * 0.1,
    ry: CELL * 0.1,
    fill: paint.solid,
    ...(highlighted ? { stroke: HIGHLIGHT, strokeWidth: Math.max(3, CELL * 0.07) } : {}),
  });
  fills.push({ obj: cell, fill: paint.fill, fallback: paint.solid });
  out.push(cell);

  out.push(text(String(e.z), {
    x: ox + size * 0.1,
    y: oy + size * 0.08,
    size: CELL * 0.16,
    font: cs.numberFont,
    fill: ink,
    weight: '600',
  }));

  const hasSub = cs.showNames || cs.showMasses;
  out.push(text(e.symbol, {
    x: ox + size / 2,
    y: oy + size * (hasSub ? 0.4 : 0.5),
    size: CELL * (hasSub ? 0.32 : 0.44),
    font: cs.symbolFont,
    fill: ink,
    originX: 'center',
    originY: 'center',
    weight: 'bold',
  }));

  if (cs.showNames) {
    const nm = fitFont(e.name, CELL * 0.135, size * 0.9, 0.55);
    out.push(text(e.name, {
      x: ox + size / 2,
      y: oy + size * 0.66,
      size: nm,
      font: cs.nameFont,
      fill: ink,
      originX: 'center',
      originY: 'center',
    }));
  }
  if (cs.showMasses) {
    out.push(text(e.mass, {
      x: ox + size / 2,
      y: oy + size * 0.86,
      size: CELL * 0.11,
      font: cs.massFont,
      fill: ink,
      originX: 'center',
      originY: 'center',
    }));
  }
}

/** A group-3 f-block placeholder (e.g. "57–71" + "*") in the main grid. Paints
 *  from the lanthanide/actinide family fill so recoloring that family carries
 *  through to its marker cell too. */
function drawPlaceholder(
  x: number,
  y: number,
  marker: string,
  range: string,
  paint: CellPaint,
  font: string,
  out: FabricObject[],
  fills: FillJob[],
): void {
  const ink = readableInkOn(paint.solid);
  const rect = new Rect({ left: x, top: y, width: CELL, height: CELL, rx: CELL * 0.1, ry: CELL * 0.1, fill: paint.solid });
  fills.push({ obj: rect, fill: paint.fill, fallback: paint.solid });
  out.push(rect);
  out.push(text(marker, {
    x: x + CELL / 2, y: y + CELL * 0.34, size: CELL * 0.3, font, fill: ink,
    originX: 'center', originY: 'center', weight: 'bold',
  }));
  out.push(text(range, {
    x: x + CELL / 2, y: y + CELL * 0.68, size: CELL * 0.15, font, fill: ink,
    originX: 'center', originY: 'center',
  }));
}

const cellLeft = (col0: number): number => col0 * PITCH + GAP / 2;

// The family fills show only in family mode, the state fills only in state mode
// (the 'Family colors' section reads as "the coloring section" either way).
const CATEGORY_WHEN = { key: 'colorBy', equals: 'category' } as const;
const STATE_WHEN = { key: 'colorBy', equals: 'state' } as const;

const periodicTable: ToolRecipe = {
  id: 'periodic-table',
  domain: 'science',
  tags: ['periodic-table', 'chemistry', 'science', 'elements', 'reference', 'anchor-chart', 'poster'],
  featured: true,
  name: 'Periodic Table',
  category: 'science',
  version: 1,
  description:
    'The full periodic table — all 118 elements in the 18-column layout with the lanthanides and actinides below. Color by element family or by state, show names and masses, highlight a set of elements, and add a legend.',
  defaults: {
    title: 'The Periodic Table of the Elements',
    colorBy: 'category',
    showNames: true,
    showMasses: false,
    showLegend: true,
    highlight: '',
    titleFont: DEFAULT_FONT,
    titleColor: INK,
    textFont: DEFAULT_FONT,
    // Per-family cell colors (BUILD A). Defaults are the exact CATEGORY_COLORS
    // hexes, so an untouched table paints byte-identically to the old scheme.
    famAlkali: CATEGORY_COLORS['alkali-metal'],
    famAlkalineEarth: CATEGORY_COLORS['alkaline-earth'],
    famTransition: CATEGORY_COLORS['transition-metal'],
    famPostTransition: CATEGORY_COLORS['post-transition-metal'],
    famMetalloid: CATEGORY_COLORS.metalloid,
    famNonmetal: CATEGORY_COLORS['reactive-nonmetal'],
    famHalogen: CATEGORY_COLORS.halogen,
    famNobleGas: CATEGORY_COLORS['noble-gas'],
    famLanthanide: CATEGORY_COLORS.lanthanide,
    famActinide: CATEGORY_COLORS.actinide,
    famUnknown: CATEGORY_COLORS.unknown,
    // Per-state cell colors (BUILD A). Defaults are the internal STATE_COLORS.
    stateSolidFill: STATE_COLORS.solid,
    stateLiquidFill: STATE_COLORS.liquid,
    stateGasFill: STATE_COLORS.gas,
    // Per-part fonts (BUILD B) — '' means "inherit textFont" (resolved at
    // generate). Empty defaults keep every saved table + grade band identical.
    symbolFont: '',
    nameFont: '',
    massFont: '',
    numberFont: '',
    legendFont: '',
  },
  schema: [
    { key: 'title', label: 'Title', type: 'text', section: 'What to show' },
    {
      key: 'colorBy',
      label: 'Color the cells by',
      type: 'select',
      section: 'What to show',
      options: [
        { value: 'category', label: 'Element family' },
        { value: 'state', label: 'State (solid / liquid / gas)' },
      ],
    },
    {
      key: 'highlight',
      label: 'Highlight elements',
      type: 'text',
      section: 'What to show',
      help: 'Symbols to ring, separated by spaces or commas — e.g. "H, O, Na". Case-insensitive.',
    },
    { key: 'showNames', label: 'Show element names', type: 'boolean', section: 'What to show' },
    { key: 'showMasses', label: 'Show atomic masses', type: 'boolean', section: 'What to show' },
    { key: 'showLegend', label: 'Show color legend', type: 'boolean', section: 'What to show' },

    // Family colors (BUILD A) — one fill per family; shown in family mode. Labels
    // are the CATEGORY_LABELS text so a swatch matches its legend row exactly.
    { key: 'famAlkali', label: CATEGORY_LABELS['alkali-metal'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famAlkalineEarth', label: CATEGORY_LABELS['alkaline-earth'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famTransition', label: CATEGORY_LABELS['transition-metal'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famPostTransition', label: CATEGORY_LABELS['post-transition-metal'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famMetalloid', label: CATEGORY_LABELS.metalloid, type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famNonmetal', label: CATEGORY_LABELS['reactive-nonmetal'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famHalogen', label: CATEGORY_LABELS.halogen, type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famNobleGas', label: CATEGORY_LABELS['noble-gas'], type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famLanthanide', label: CATEGORY_LABELS.lanthanide, type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famActinide', label: CATEGORY_LABELS.actinide, type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    { key: 'famUnknown', label: CATEGORY_LABELS.unknown, type: 'fill', section: 'Family colors', visibleWhen: CATEGORY_WHEN },
    // State colors (BUILD A) — shown in state mode, same section.
    { key: 'stateSolidFill', label: STATE_LABELS.solid, type: 'fill', section: 'Family colors', visibleWhen: STATE_WHEN },
    { key: 'stateLiquidFill', label: STATE_LABELS.liquid, type: 'fill', section: 'Family colors', visibleWhen: STATE_WHEN },
    { key: 'stateGasFill', label: STATE_LABELS.gas, type: 'fill', section: 'Family colors', visibleWhen: STATE_WHEN },

    // Fonts (BUILD B) — titleFont keeps its own family; every cell/legend part
    // defaults to '' = inherit the Text font, resolved at generate time.
    { key: 'titleFont', label: 'Title font', type: 'font', section: 'Fonts' },
    { key: 'textFont', label: 'Text font (default for all parts)', type: 'font', section: 'Fonts' },
    { key: 'symbolFont', label: 'Symbol font', type: 'font', section: 'Fonts' },
    { key: 'nameFont', label: 'Name font', type: 'font', section: 'Fonts' },
    { key: 'massFont', label: 'Mass font', type: 'font', section: 'Fonts' },
    { key: 'numberFont', label: 'Number font', type: 'font', section: 'Fonts' },
    { key: 'legendFont', label: 'Legend font', type: 'font', section: 'Fonts' },

    { key: 'titleColor', label: 'Title color', type: 'color', section: 'Style' },
  ],
  // Hero the three biggest decisions (heading, color scheme, what to spotlight);
  // the rest reads two-up: What to show, the coloring section (family OR state
  // fills by colorBy), Fonts, then the lone Style knob (title color).
  modal: {
    hero: ['title', 'colorBy', 'highlight'],
    columns: 2,
    sectionOrder: ['What to show', 'Family colors', 'Fonts', 'Style'],
  },
  // Honest band values: youngest = symbols only, big; detail (names → legend →
  // masses) accretes with reading maturity. Faces walk the house ladder
  // Fredoka → Nunito → Lexend → Arial.
  gradeBands: {
    K2: { showNames: false, showMasses: false, showLegend: false, titleFont: 'Fredoka', textFont: 'Fredoka' },
    G35: { showNames: true, showMasses: false, showLegend: false, titleFont: 'Nunito', textFont: 'Nunito' },
    G68: { showNames: true, showMasses: false, showLegend: true, titleFont: 'Lexend', textFont: 'Lexend' },
    G912: { showNames: true, showMasses: true, showLegend: true, titleFont: 'Arial', textFont: 'Arial' },
  },
  async generate(params) {
    const title = rawStr(params.title, 'The Periodic Table of the Elements');
    const colorByRaw = str(params.colorBy, 'category');
    const colorBy: 'category' | 'state' = colorByRaw === 'state' ? 'state' : 'category';
    const showNames = bool(params.showNames, true);
    const showMasses = bool(params.showMasses, false);
    const showLegend = bool(params.showLegend, true);
    const titleFont = str(params.titleFont, DEFAULT_FONT);
    const titleColor = str(params.titleColor, INK);
    const textFont = str(params.textFont, DEFAULT_FONT);
    // Per-part fonts (BUILD B): each inherits textFont when unset ('' or absent),
    // so every table saved before this — and every grade band (bands set only
    // textFont) — renders byte-identically.
    const partFont = (key: string): string => {
      const v = params[key];
      return typeof v === 'string' && v.trim() !== '' ? v : textFont;
    };
    const symbolFont = partFont('symbolFont');
    const nameFont = partFont('nameFont');
    const massFont = partFont('massFont');
    const numberFont = partFont('numberFont');
    const legendFont = partFont('legendFont');

    const highlightSet = new Set(
      rawStr(params.highlight, '')
        .split(/[\s,]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
    );
    const isHot = (e: ElementInfo): boolean => highlightSet.has(e.symbol.toUpperCase());

    const cs: CellStyle = { colorBy, showNames, showMasses, symbolFont, nameFont, massFont, numberFont };
    const objects: FabricObject[] = [];
    const fills: FillJob[] = [];
    // A cell's fill resolves from its family (or state) fill param, falling back
    // to the scheme hex; drawCell registers the (possibly gradient/texture) fill.
    const cellPaintFor = (e: ElementInfo): CellPaint =>
      colorBy === 'state'
        ? pickFill(params, STATE_PARAM_KEY[e.state], STATE_COLORS[e.state] || CELL_FALLBACK)
        : pickFill(params, FAM_PARAM_KEY[e.category], CATEGORY_COLORS[e.category] || CELL_FALLBACK);

    // Title.
    if (title.trim() !== '') {
      objects.push(text(title, {
        x: OVERALL_W / 2,
        y: TITLE_H * 0.5,
        size: fitFont(title, TITLE_H * 0.5, OVERALL_W * 0.92, 0.55),
        font: titleFont,
        fill: titleColor,
        originX: 'center',
        originY: 'center',
        weight: 'bold',
      }));
    }

    // Placements: main grid (group != null) + the two detached f-block strips.
    const placements: { e: ElementInfo; x: number; y: number }[] = [];
    for (const e of ELEMENTS) {
      if (e.group != null && e.period >= 1 && e.period <= MAIN_ROWS) {
        placements.push({
          e,
          x: cellLeft(e.group - 1),
          y: GRID_TOP + (e.period - 1) * PITCH + GAP / 2,
        });
      }
    }
    const lanthanides = ELEMENTS.filter((e) => e.z >= 57 && e.z <= 71);
    const actinides = ELEMENTS.filter((e) => e.z >= 89 && e.z <= 103);
    lanthanides.forEach((e, i) => {
      placements.push({ e, x: cellLeft(FBLOCK_COL0 + i), y: LANTH_TOP + GAP / 2 });
    });
    actinides.forEach((e, i) => {
      placements.push({ e, x: cellLeft(FBLOCK_COL0 + i), y: ACTIN_TOP + GAP / 2 });
    });

    // f-block markers in the main grid's group-3 cells (period 6/7) + a leading
    // marker on each detached strip, tying them together. The placeholder cells
    // wear the lanthanide/actinide family fill so a recolor carries through.
    const lanthPaint = pickFill(params, FAM_PARAM_KEY.lanthanide, CATEGORY_COLORS.lanthanide || CELL_FALLBACK);
    const actinPaint = pickFill(params, FAM_PARAM_KEY.actinide, CATEGORY_COLORS.actinide || CELL_FALLBACK);
    drawPlaceholder(cellLeft(FBLOCK_COL0), GRID_TOP + 5 * PITCH + GAP / 2, '*', '57–71', lanthPaint, textFont, objects, fills);
    drawPlaceholder(cellLeft(FBLOCK_COL0), GRID_TOP + 6 * PITCH + GAP / 2, '**', '89–103', actinPaint, textFont, objects, fills);
    objects.push(text('*', {
      x: cellLeft(FBLOCK_COL0 - 1) + CELL / 2, y: LANTH_TOP + GAP / 2 + CELL / 2,
      size: CELL * 0.4, font: textFont, fill: INK, originX: 'center', originY: 'center', weight: 'bold',
    }));
    objects.push(text('**', {
      x: cellLeft(FBLOCK_COL0 - 1) + CELL / 2, y: ACTIN_TOP + GAP / 2 + CELL / 2,
      size: CELL * 0.4, font: textFont, fill: INK, originX: 'center', originY: 'center', weight: 'bold',
    }));

    // Cool cells first, hot (highlighted) cells last so their pop + ring sit on
    // top of any neighbor a scaled-up cell overlaps.
    for (const p of placements) if (!isHot(p.e)) drawCell(p.e, p.x, p.y, cs, false, cellPaintFor(p.e), objects, fills);
    for (const p of placements) if (isHot(p.e)) drawCell(p.e, p.x, p.y, cs, true, cellPaintFor(p.e), objects, fills);

    // Legend (category families, or the three states). Swatches reuse the SAME
    // resolved fills as the cells, so a recolored family shows its true fill.
    let bottom = DETACH_BOTTOM;
    if (showLegend) {
      const entries: { paint: CellPaint; label: string }[] = colorBy === 'state'
        ? (['solid', 'liquid', 'gas'] as const).map((s) => ({
          paint: pickFill(params, STATE_PARAM_KEY[s], STATE_COLORS[s] || CELL_FALLBACK),
          label: STATE_LABELS[s],
        }))
        : (Object.keys(CATEGORY_LABELS) as ElementCategory[]).map((k) => ({
          paint: pickFill(params, FAM_PARAM_KEY[k], CATEGORY_COLORS[k] || CELL_FALLBACK),
          label: CATEGORY_LABELS[k],
        }));
      const legendTop = DETACH_BOTTOM + PITCH * 0.4;
      const itemW = OVERALL_W / LEGEND_PER_ROW;
      const sw = LEGEND_ROW_H * 0.55;
      entries.forEach((entry, i) => {
        const row = Math.floor(i / LEGEND_PER_ROW);
        const col = i % LEGEND_PER_ROW;
        const ex = col * itemW + GAP;
        const ey = legendTop + row * LEGEND_ROW_H;
        const swatch = new Rect({
          left: ex, top: ey + (LEGEND_ROW_H - sw) / 2, width: sw, height: sw,
          rx: sw * 0.2, ry: sw * 0.2, fill: entry.paint.solid,
        });
        fills.push({ obj: swatch, fill: entry.paint.fill, fallback: entry.paint.solid });
        objects.push(swatch);
        objects.push(text(entry.label, {
          x: ex + sw + 12,
          y: ey + LEGEND_ROW_H / 2,
          size: fitFont(entry.label, LEGEND_ROW_H * 0.42, itemW - sw - 24, 0.55),
          font: legendFont,
          fill: INK,
          originY: 'center',
        }));
      });
      const legendRows = Math.ceil(entries.length / LEGEND_PER_ROW);
      bottom = legendTop + legendRows * LEGEND_ROW_H;
    }

    // A transparent spacer pins the group's bottom edge (title/legend widths
    // never exceed the 18-column grid, so the width is already OVERALL_W).
    objects.push(new Rect({
      left: 0, top: bottom + PITCH * 0.2, width: OVERALL_W, height: 1, fill: '', stroke: '',
    }));

    // Resolve every family/state fill once (a solid resolves to itself →
    // byte-identical to the old inline fill; a gradient/texture to a fabric
    // Gradient/Pattern with the solid as its dead-URL fallback).
    await Promise.all(fills.map((j) => applyFill(j.obj, j.fill, j.fallback)));

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, periodicTable, params);
  },
};

registerRecipe(periodicTable);
